/**
 * buildAdvisorFromReports
 * ------------------------
 * Adapter qui transforme les vraies données utilisateur (Redux user +
 * MonBilanPatrimonialReports) en données pour le ConseillerPatrimonialPage.
 *
 * Plus de mock. Le score, le diagnostic, les actions prioritaires et la
 * projection 20 ans sont calculés côté front à partir des chiffres réels
 * de l'utilisateur connecté. Quand le backend exposera des endpoints LLM
 * dédiés (compute-score, generate-advice), ils remplaceront ce calcul.
 *
 * LAP-i18n — Toutes les chaînes affichées sont maintenant produites via la
 * fonction de traduction `t` injectée par le composant React. Les composants
 * recomputent ce builder lorsque `i18n.language` change.
 *
 * Référence règles métier : utils/scoringEngine.js
 */

import {
  scoreToGrade,
  scoreEpargnePrecaution,
  scoreDiversification,
  scoreEndettement,
  scorePrevoyance,
  scoreFiscalite,
  scoreObjectifs,
  computeGlobalScore,
} from './scoringEngine'

const ASSET_LABELS = {
  // mapping commun des libellés Django vers nos clés internes
  'Compte courant': 'compte_courant',
  'Livret A': 'livret_a',
  LDDS: 'ldds',
  Livret: 'livret_a',
  'Assurance Vie': 'assurance_vie',
  'Assurance-vie': 'assurance_vie',
  AV: 'assurance_vie',
  PEA: 'pea',
  PER: 'per',
  Immobilier: 'immobilier',
  'Immobilier locatif': 'immobilier',
  'Résidence principale': 'immobilier',
  Crypto: 'crypto',
  Actions: 'actions',
  Obligations: 'obligations',
}

const SAFE_LIQUIDITY_KEYS = ['compte_courant', 'livret_a', 'ldds', 'livret']

// LAP-i18n — Identity fallback used when the page is rendered before a real
// t function is provided. Returns the defaultValue when present.
const identityT = (key, opts = {}) => {
  if (opts && typeof opts.defaultValue === 'string') return opts.defaultValue
  return key
}

export function buildAdvisorFromReports({ reports = [], user = {}, t = identityT } = {}) {
  // ─────── Extraction des sections ───────
  const reportByType = {}
  reports.forEach((r) => {
    if (r?.type) reportByType[r.type] = r.data
  })

  const portrait = reportByType['mon-portrait'] || {}
  const profil = reportByType['profil-investisseure'] || {}
  const budget = reportByType['mon-budget'] || {}
  const patrimoine = reportByType['mon-patrimoine'] || {}
  const patrimoineNet = reportByType['patrimoine-net'] || {}
  const objectifsRaw = reportByType['mes-objectifs'] || []

  const firstName =
    user.first_name || user.firstName || user.prenom || portrait.prenom || ''
  const lastName = user.last_name || user.nom || portrait.nom || ''
  const ageRaw =
    user.age ||
    portrait.age ||
    computeAgeFromBirthdate(user.date_de_naissance || portrait.date_de_naissance)
  const age = parseInt(ageRaw, 10) || null
  const city = user.vile || portrait.vile || ''
  const children = parseInt(user.nombre_enfants ?? portrait.nombre_enfants, 10) || 0

  // ─────── Budget / cashflow ───────
  const monthlyIncome = num(budget.avarage_income, 0)
  const monthlyExpenses = num(budget.depense, 0)
  const monthlyTaxes = num(budget.impots, 0)
  const monthlyCashflow = num(
    budget.cashflow,
    monthlyIncome - monthlyExpenses - monthlyTaxes,
  )
  const lastTaxRate = num(budget.last_tax_rate, null)

  // ─────── Patrimoine ───────
  const actifs = patrimoine.actifs || { labels: [], data: [], total: 0 }
  const passifs = patrimoine.passifs || { labels: [], data: [], total: 0 }
  const totalActifs = num(actifs.total, sum(actifs.data))
  const totalPassifs = num(passifs.total, sum(passifs.data))
  const patrimoineNetValue = num(patrimoineNet.total, totalActifs - totalPassifs)

  // Répartition normalisée (clés internes)
  const repartition = buildRepartition(actifs.labels, actifs.data, totalActifs)

  const liquiditesDisponibles = SAFE_LIQUIDITY_KEYS.reduce(
    (acc, k) => acc + (repartition._absolute?.[k] || 0),
    0,
  )

  // ─────── Objectifs ───────
  const objectifsDefinis = Array.isArray(objectifsRaw) ? objectifsRaw : []

  // ─────── Profil investisseur ───────
  const risqueScore = num(profil.risque_score, null)
  const financiereScore = num(profil.financiere_score, null)
  const financeInterests = Array.isArray(profil.finance_interest)
    ? profil.finance_interest
    : []

  // ─────── Dimensions du score ───────
  const dimensions = [
    {
      key: 'epargne',
      label: t('conseillerPatrimonial.dimensions.epargne'),
      weight: 0.2,
      score: scoreEpargnePrecaution({
        liquiditesDisponibles,
        depensesMensuelles: monthlyExpenses,
      }),
      detail: epargneDetail(t, liquiditesDisponibles, monthlyExpenses),
      status: statusForScore(
        scoreEpargnePrecaution({
          liquiditesDisponibles,
          depensesMensuelles: monthlyExpenses,
        }),
      ),
    },
    {
      key: 'diversification',
      label: t('conseillerPatrimonial.dimensions.diversification'),
      weight: 0.2,
      score: scoreDiversificationFromRepartition(repartition),
      detail: diversificationDetail(t, repartition),
      status: statusForScore(scoreDiversificationFromRepartition(repartition)),
    },
    {
      key: 'endettement',
      label: t('conseillerPatrimonial.dimensions.endettement'),
      weight: 0.15,
      score: scoreEndettement({
        patrimoineNet: patrimoineNetValue,
        revenuMensuel: monthlyIncome,
        mensualitesCredits: 0, // TODO : remonter depuis backend si dispo
      }),
      detail: endettementDetail(t, patrimoineNetValue, monthlyCashflow),
      status: statusForScore(
        scoreEndettement({
          patrimoineNet: patrimoineNetValue,
          revenuMensuel: monthlyIncome,
          mensualitesCredits: 0,
        }),
      ),
    },
    {
      key: 'prevoyance',
      label: t('conseillerPatrimonial.dimensions.prevoyance'),
      weight: 0.2,
      score: scorePrevoyance({
        aPer: (repartition._absolute?.per || 0) > 0,
        anciennetéAv: (repartition._absolute?.assurance_vie || 0) > 0 ? 1 : 0,
        aPrevoyance: false, // TODO backend : exposer flag prévoyance
        age,
      }),
      detail: prevoyanceDetail(t, repartition, age),
      status: statusForScore(
        scorePrevoyance({
          aPer: (repartition._absolute?.per || 0) > 0,
          anciennetéAv: (repartition._absolute?.assurance_vie || 0) > 0 ? 1 : 0,
          aPrevoyance: false,
          age,
        }),
      ),
    },
    {
      key: 'fiscalite',
      label: t('conseillerPatrimonial.dimensions.fiscalite'),
      weight: 0.15,
      score: scoreFiscaliteFromTaxRate({
        lastTaxRate,
        annualIncome: monthlyIncome * 12,
        hasPer: (repartition._absolute?.per || 0) > 0,
      }),
      detail: fiscaliteDetail(t, {
        lastTaxRate,
        annualIncome: monthlyIncome * 12,
        hasPer: (repartition._absolute?.per || 0) > 0,
      }),
      status: statusForScore(
        scoreFiscaliteFromTaxRate({
          lastTaxRate,
          annualIncome: monthlyIncome * 12,
          hasPer: (repartition._absolute?.per || 0) > 0,
        }),
      ),
    },
    {
      key: 'objectifs',
      label: t('conseillerPatrimonial.dimensions.objectifs'),
      weight: 0.1,
      score: scoreObjectifs({
        objectifsDefinis,
        repartitionVsCible: 0.5,
      }),
      detail: objectifsDetail(t, objectifsDefinis),
      status: statusForScore(
        scoreObjectifs({ objectifsDefinis, repartitionVsCible: 0.5 }),
      ),
    },
  ]

  const globalScore = computeGlobalScore(dimensions)
  const grade = scoreToGrade(globalScore)

  // ─────── Forces / Faiblesses / Risques ───────
  const diagnostic = buildDiagnostic({
    t,
    age,
    monthlyCashflow,
    monthlyIncome,
    patrimoineNetValue,
    repartition,
    dimensions,
    liquiditesDisponibles,
    monthlyExpenses,
  })

  // ─────── Actions prioritaires ───────
  const actionsPrioritaires = buildActionsPrioritaires({
    t,
    age,
    monthlyIncome,
    monthlyCashflow,
    lastTaxRate,
    repartition,
    liquiditesDisponibles,
    monthlyExpenses,
    objectifsDefinis,
    patrimoineNetValue,
    city,
  })

  // ─────── Projets créables ───────
  const projetsSugeres = buildProjets({
    t,
    monthlyCashflow,
    repartition,
    age,
    monthlyExpenses,
    city,
  })

  // ─────── Projection 20 ans ───────
  const projection20ans = buildProjection20ans({
    patrimoineNetValue,
    monthlyCashflow,
  })

  // ─────── Suggested prompts (personnalisés au profil) ───────
  const suggestedPrompts = buildSuggestedPrompts({
    t,
    objectifsDefinis,
    age,
    repartition,
    monthlyCashflow,
  })

  // LAP-748 — Détection heuristique : le cashflow et les chiffres budget sont
  // "théoriques" (issus de la cible de répartition utilisateur) tant qu'aucune
  // opération bancaire n'est importée. Signaux qui indiquent du théorique :
  //  - aucun actif renseigné (totalActifs === 0)
  //  - aucun passif renseigné (totalPassifs === 0)
  //  - patrimoineNet quasi nul
  // Quand l'utilisateur importe ses opérations, le backend recalcule le
  // cashflow à partir des vraies transactions et ces conditions tombent.
  // TODO BACKEND : exposer un champ explicite `has_operations` ou
  // `is_cashflow_theoretical` dans la réponse `mon-budget` pour une
  // détection fiable au lieu d'heuristique.
  const isCashflowTheoretical =
    totalActifs === 0 && totalPassifs === 0 && Math.abs(patrimoineNetValue) < 100

  return {
    userProfile: {
      firstName,
      lastName,
      age,
      city,
      children,
      monthlyIncome,
      monthlyExpenses,
      netWorth: patrimoineNetValue,
      cashflow: monthlyCashflow,
      hasInvestmentProfile: risqueScore != null && financiereScore != null,
      hasObjectives: objectifsDefinis.length > 0,
      risqueScore,
      financiereScore,
      financeInterests,
      isCashflowTheoretical, // LAP-748
    },
    patrimoineScore: {
      global: globalScore,
      grade,
      trend: '0', // TODO backend : exposer le delta vs mois dernier
      percentile: estimatePercentile(globalScore),
      dimensions,
    },
    diagnostic,
    actionsPrioritaires,
    projetsSugeres,
    projection20ans,
    suggestedPrompts,
    // LAP-748 — Indicateurs de complétude, tolérants à plusieurs nominations
    // backend. Le bandeau "Complétez votre profil" affiche un CTA UNIQUEMENT
    // pour chaque champ vraiment manquant ; si tout est rempli, le bandeau
    // disparaît entièrement.
    completeness: {
      hasPortrait: !!firstName && !!age,
      // Le profil investisseur est considéré rempli si l'un des scores est
      // présent OU si des centres d'intérêts ont été sélectionnés (ce qui
      // implique que le questionnaire a été parcouru).
      hasProfilInvestisseur:
        risqueScore != null ||
        financiereScore != null ||
        (profil.risque !== undefined && profil.risque !== null) ||
        (profil.risk_score !== undefined && profil.risk_score !== null) ||
        financeInterests.length > 0,
      hasFinanceInterests: financeInterests.length > 0,
      hasBudget: monthlyIncome > 0,
      // Le patrimoine est considéré rempli dès qu'au moins UNE ligne d'actif
      // existe (même montant 0€). Le user a rempli sa card, c'est ce qui compte.
      hasPatrimoine:
        totalActifs > 0 ||
        (Array.isArray(actifs.labels) && actifs.labels.length > 0) ||
        (Array.isArray(actifs.data) && actifs.data.length > 0),
      hasObjectifs: objectifsDefinis.length > 0,
    },
  }
}

// ──────────────────────────────────────────────────────────────────
// Helpers privés
// ──────────────────────────────────────────────────────────────────

function num(v, fallback = 0) {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : fallback
}

function sum(arr) {
  if (!Array.isArray(arr)) return 0
  return arr.reduce((a, b) => a + (parseFloat(b) || 0), 0)
}

function computeAgeFromBirthdate(d) {
  if (!d) return null
  const birth = new Date(d)
  if (isNaN(birth.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - birth.getFullYear()
  if (
    now.getMonth() < birth.getMonth() ||
    (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate())
  ) {
    age--
  }
  return age
}

function buildRepartition(labels = [], data = [], total = 0) {
  const ratios = {}
  const absolute = {}
  if (!total) total = sum(data)
  if (!total) return { _absolute: absolute }
  ;(labels || []).forEach((label, i) => {
    const key = ASSET_LABELS[label] || slugify(label)
    const value = parseFloat(data[i]) || 0
    ratios[key] = (ratios[key] || 0) + value / total
    absolute[key] = (absolute[key] || 0) + value
  })
  ratios._absolute = absolute
  return ratios
}

function slugify(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
}

function statusForScore(score) {
  if (score >= 70) return 'good'
  if (score >= 40) return 'warning'
  return 'critical'
}

function scoreDiversificationFromRepartition(repartition) {
  const ratios = Object.entries(repartition)
    .filter(([k]) => !k.startsWith('_'))
    .map(([, v]) => v)
  if (ratios.length === 0) return 0
  const hhi = ratios.reduce((acc, v) => acc + v * v, 0)
  return Math.round((1 - hhi) * 100)
}

function scoreFiscaliteFromTaxRate({ lastTaxRate, annualIncome, hasPer }) {
  // Si lastTaxRate fourni, on s'en sert. Sinon estimation par tranche.
  let tmi = lastTaxRate != null ? lastTaxRate / 100 : estimateTmi(annualIncome)
  if (tmi < 0.11) return 80
  let s = 20
  if (hasPer) s += 30
  return Math.min(100, s)
}

function estimateTmi(annualIncome) {
  // Barème 2026 France, célibataire, approximation grossière
  if (!annualIncome || annualIncome < 11500) return 0
  if (annualIncome < 29000) return 0.11
  if (annualIncome < 83000) return 0.3
  if (annualIncome < 180000) return 0.41
  return 0.45
}

function epargneDetail(t, liquidites, depensesMensuelles) {
  if (!depensesMensuelles) {
    return t('conseillerPatrimonial.dimensionDetails.epargne.noBudget')
  }
  const mois = liquidites / depensesMensuelles
  const months = mois.toFixed(1)
  if (mois >= 6) return t('conseillerPatrimonial.dimensionDetails.epargne.excellent', { months })
  if (mois >= 3) return t('conseillerPatrimonial.dimensionDetails.epargne.good', { months })
  if (mois >= 1)
    return t('conseillerPatrimonial.dimensionDetails.epargne.warning', { months })
  return t('conseillerPatrimonial.dimensionDetails.epargne.critical', { months })
}

function diversificationDetail(t, repartition) {
  const sorted = Object.entries(repartition)
    .filter(([k]) => !k.startsWith('_'))
    .sort(([, a], [, b]) => b - a)
  if (sorted.length === 0) {
    return t('conseillerPatrimonial.dimensionDetails.diversification.empty')
  }
  const [topKey, topPct] = sorted[0]
  if (topPct > 0.7) {
    return t('conseillerPatrimonial.dimensionDetails.diversification.concentrated', {
      pct: Math.round(topPct * 100),
      label: prettifyKey(t, topKey),
    })
  }
  if (sorted.length < 3) {
    if (sorted.length === 1)
      return t('conseillerPatrimonial.dimensionDetails.diversification.fewSingle')
    return t('conseillerPatrimonial.dimensionDetails.diversification.fewMultiple', {
      n: sorted.length,
    })
  }
  return t('conseillerPatrimonial.dimensionDetails.diversification.balanced', {
    n: sorted.length,
  })
}

function endettementDetail(t, patrimoineNet, cashflow) {
  if (patrimoineNet < 0) {
    return t('conseillerPatrimonial.dimensionDetails.endettement.negative', {
      amount: formatEuro(patrimoineNet),
    })
  }
  if (cashflow > 0) {
    return t('conseillerPatrimonial.dimensionDetails.endettement.positive', {
      amount: formatEuro(cashflow),
    })
  }
  return t('conseillerPatrimonial.dimensionDetails.endettement.weak', {
    amount: formatEuro(cashflow),
  })
}

function prevoyanceDetail(t, repartition, age) {
  const hasPer = (repartition._absolute?.per || 0) > 0
  const hasAv = (repartition._absolute?.assurance_vie || 0) > 0
  if (hasPer && hasAv) return t('conseillerPatrimonial.dimensionDetails.prevoyance.perAndAv')
  if (hasPer) return t('conseillerPatrimonial.dimensionDetails.prevoyance.onlyPer')
  if (hasAv) return t('conseillerPatrimonial.dimensionDetails.prevoyance.onlyAv')
  if (age && age < 35)
    return t('conseillerPatrimonial.dimensionDetails.prevoyance.youngNone')
  return t('conseillerPatrimonial.dimensionDetails.prevoyance.none')
}

function fiscaliteDetail(t, { lastTaxRate, annualIncome, hasPer }) {
  const tmi = lastTaxRate != null ? lastTaxRate : estimateTmi(annualIncome) * 100
  if (tmi < 11) return t('conseillerPatrimonial.dimensionDetails.fiscalite.lowTmi')
  if (hasPer)
    return t('conseillerPatrimonial.dimensionDetails.fiscalite.hasPer', {
      tmi: tmi.toFixed(0),
    })
  return t('conseillerPatrimonial.dimensionDetails.fiscalite.noPer', {
    tmi: tmi.toFixed(0),
  })
}

function objectifsDetail(t, objectifsDefinis) {
  if (objectifsDefinis.length === 0)
    return t('conseillerPatrimonial.dimensionDetails.objectifs.none')
  if (objectifsDefinis.length === 1)
    return t('conseillerPatrimonial.dimensionDetails.objectifs.definedSingle')
  return t('conseillerPatrimonial.dimensionDetails.objectifs.definedMultiple', {
    n: objectifsDefinis.length,
  })
}

function prettifyKey(t, key) {
  const translated = t(`conseillerPatrimonial.assetLabels.${key}`, { defaultValue: '' })
  if (translated) return translated
  return key.replace(/_/g, ' ')
}

function estimatePercentile(score) {
  // Approximation grossière : distribution normale centrée sur 50
  if (score >= 90) return 95
  if (score >= 80) return 85
  if (score >= 70) return 75
  if (score >= 55) return 60
  if (score >= 40) return 40
  if (score >= 25) return 22
  return 10
}

function formatEuro(n) {
  if (n == null) return '—'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `${n < 0 ? '-' : ''}${(abs / 1_000_000).toFixed(1)} M€`
  if (abs >= 1_000) return `${n < 0 ? '-' : ''}${(abs / 1_000).toFixed(0)} k€`
  return `${Math.round(n)} €`
}

function buildDiagnostic({
  t,
  age,
  monthlyCashflow,
  monthlyIncome,
  patrimoineNetValue,
  repartition,
  liquiditesDisponibles,
  monthlyExpenses,
}) {
  const forces = []
  const faiblesses = []
  const risques = []

  // Forces
  if (monthlyCashflow > 0) {
    const rate = monthlyIncome ? Math.round((monthlyCashflow / monthlyIncome) * 100) : null
    forces.push({
      id: 'f-cashflow',
      title: t('conseillerPatrimonial.forces.cashflow.title'),
      description:
        rate != null
          ? t('conseillerPatrimonial.forces.cashflow.descWithRate', {
              amount: formatEuro(monthlyCashflow),
              rate,
            })
          : t('conseillerPatrimonial.forces.cashflow.descNoRate', {
              amount: formatEuro(monthlyCashflow),
            }),
      icon: 'TrendingUp',
    })
  }
  if (age && age < 35) {
    forces.push({
      id: 'f-jeune',
      title: t('conseillerPatrimonial.forces.young.title'),
      description: t('conseillerPatrimonial.forces.young.desc', { age }),
      icon: 'AccessTime',
    })
  }
  if (monthlyIncome > 4000) {
    forces.push({
      id: 'f-revenus',
      title: t('conseillerPatrimonial.forces.income.title'),
      description: t('conseillerPatrimonial.forces.income.desc', {
        amount: formatEuro(monthlyIncome),
      }),
      icon: 'AccountBalance',
    })
  }
  if (Object.keys(repartition).filter((k) => !k.startsWith('_')).length >= 3) {
    forces.push({
      id: 'f-diversification',
      title: t('conseillerPatrimonial.forces.diversification.title'),
      description: t('conseillerPatrimonial.forces.diversification.desc'),
      icon: 'PieChart',
    })
  }

  // Faiblesses
  if (patrimoineNetValue < 0) {
    faiblesses.push({
      id: 'w-net-negatif',
      title: t('conseillerPatrimonial.faiblesses.netNegative.title'),
      severity: 'high',
      description: t('conseillerPatrimonial.faiblesses.netNegative.desc', {
        amount: formatEuro(patrimoineNetValue),
      }),
      icon: 'TrendingDown',
    })
  }
  const compteCourantRatio = repartition.compte_courant || 0
  if (compteCourantRatio > 0.5) {
    faiblesses.push({
      id: 'w-compte-courant',
      title: t('conseillerPatrimonial.faiblesses.compteCourant.title'),
      severity: compteCourantRatio > 0.8 ? 'high' : 'medium',
      description: t('conseillerPatrimonial.faiblesses.compteCourant.desc', {
        pct: Math.round(compteCourantRatio * 100),
      }),
      icon: 'PieChart',
    })
  }
  if (liquiditesDisponibles < (monthlyExpenses || 0) * 3 && monthlyExpenses > 0) {
    faiblesses.push({
      id: 'w-epargne-precaution',
      title: t('conseillerPatrimonial.faiblesses.epargnePrecaution.title'),
      severity: 'high',
      description: t('conseillerPatrimonial.faiblesses.epargnePrecaution.desc', {
        liquid: formatEuro(liquiditesDisponibles),
        min: formatEuro(monthlyExpenses * 3),
        max: formatEuro(monthlyExpenses * 6),
      }),
      icon: 'Savings',
    })
  }

  // Risques
  if (liquiditesDisponibles < 2000) {
    risques.push({
      id: 'r-coup-dur',
      title: t('conseillerPatrimonial.risques.coupDur.title'),
      probability: 'high',
      impact: t('conseillerPatrimonial.risques.impactLevels.critique'),
      description: t('conseillerPatrimonial.risques.coupDur.desc'),
    })
  }
  if (compteCourantRatio > 0.5 && liquiditesDisponibles > 5000) {
    const erosion = Math.round(liquiditesDisponibles * 0.02)
    risques.push({
      id: 'r-inflation',
      title: t('conseillerPatrimonial.risques.inflation.title'),
      probability: t('conseillerPatrimonial.risques.probabilityLevels.certaine'),
      impact: t('conseillerPatrimonial.risques.impactLevels.moyen'),
      description: t('conseillerPatrimonial.risques.inflation.desc', {
        loss: formatEuro(erosion),
      }),
      annualLoss: erosion,
    })
  }
  if (
    age &&
    age >= 25 &&
    age < 60 &&
    (repartition._absolute?.per || 0) + (repartition._absolute?.assurance_vie || 0) === 0
  ) {
    risques.push({
      id: 'r-retraite',
      title: t('conseillerPatrimonial.risques.retraite.title'),
      probability: t('conseillerPatrimonial.risques.probabilityLevels.certaine'),
      impact: t('conseillerPatrimonial.risques.impactLevels.élevé'),
      description: t('conseillerPatrimonial.risques.retraite.desc'),
    })
  }

  // Garanties : au moins une force / faiblesse / risque pour ne pas avoir de colonne vide
  if (forces.length === 0) {
    forces.push({
      id: 'f-default',
      title: t('conseillerPatrimonial.forces.defaultEmpty.title'),
      description: t('conseillerPatrimonial.forces.defaultEmpty.desc'),
      icon: 'TrendingUp',
    })
  }
  if (faiblesses.length === 0) {
    faiblesses.push({
      id: 'w-default',
      title: t('conseillerPatrimonial.faiblesses.defaultOk.title'),
      severity: 'medium',
      description: t('conseillerPatrimonial.faiblesses.defaultOk.desc'),
      icon: 'TrendingUp',
    })
  }
  if (risques.length === 0) {
    risques.push({
      id: 'r-default',
      title: t('conseillerPatrimonial.risques.defaultOk.title'),
      probability: t('conseillerPatrimonial.risques.probabilityLevels.faible'),
      impact: t('conseillerPatrimonial.risques.impactLevels.faible'),
      description: t('conseillerPatrimonial.risques.defaultOk.desc'),
    })
  }

  return { forces, faiblesses, risques }
}

function buildActionsPrioritaires({
  t,
  age,
  monthlyIncome,
  monthlyCashflow,
  lastTaxRate,
  repartition,
  liquiditesDisponibles,
  monthlyExpenses,
  objectifsDefinis,
  patrimoineNetValue,
}) {
  const actions = []
  const tmi = lastTaxRate != null ? lastTaxRate / 100 : estimateTmi(monthlyIncome * 12)
  const hasPer = (repartition._absolute?.per || 0) > 0
  const hasAv = (repartition._absolute?.assurance_vie || 0) > 0
  const hasPea = (repartition._absolute?.pea || 0) > 0
  const hasLivret = (repartition._absolute?.livret_a || 0) + (repartition._absolute?.ldds || 0) > 0

  // 1) Épargne de précaution
  const monthsCouverts = monthlyExpenses ? liquiditesDisponibles / monthlyExpenses : 0
  if (monthsCouverts < 3) {
    const cible = (monthlyExpenses || 1000) * 6
    const versementMensuel = Math.min(
      Math.max(Math.round(monthlyCashflow * 0.4), 100),
      Math.round(cible / 12),
    )
    actions.push({
      id: 'a-livret',
      rank: actions.length + 1,
      title: hasLivret
        ? t('conseillerPatrimonial.actionItems.livret.titleHasLivret', {
            amount: versementMensuel,
          })
        : t('conseillerPatrimonial.actionItems.livret.titleNoLivret', {
            amount: versementMensuel,
          }),
      category: t('conseillerPatrimonial.actionItems.categories.epargnePrecaution'),
      impact: 9,
      facilite: 10,
      horizon: 'immediate',
      horizonLabel: t('conseillerPatrimonial.actionItems.livret.horizonLabel'),
      gainAnnuel: Math.round(versementMensuel * 12 * 0.03),
      gainDescription: t('conseillerPatrimonial.actionItems.livret.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 12 }),
      explanation: t('conseillerPatrimonial.actionItems.livret.explanation'),
      cta: t('conseillerPatrimonial.actionItems.livret.cta'),
      projetId: 'p-livret',
      bestFor: [
        t('conseillerPatrimonial.actionItems.livret.bestFor1'),
        t('conseillerPatrimonial.actionItems.livret.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.livret.tag1'),
        t('conseillerPatrimonial.actionItems.livret.tag2'),
        t('conseillerPatrimonial.actionItems.livret.tag3'),
      ],
    })
  }

  // 2) PER si TMI ≥ 30% et pas de PER
  if (tmi >= 0.3 && !hasPer && monthlyCashflow > 100) {
    const versementAnnuel = Math.min(8000, Math.round(monthlyCashflow * 12 * 0.4))
    const economie = Math.round(versementAnnuel * tmi)
    actions.push({
      id: 'a-per',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.per.title', { eco: economie }),
      category: t('conseillerPatrimonial.actionItems.categories.optimisationFiscale'),
      impact: 8,
      facilite: 7,
      horizon: 'short',
      horizonLabel: t('conseillerPatrimonial.actionItems.per.horizonLabel'),
      gainAnnuel: economie,
      gainDescription: t('conseillerPatrimonial.actionItems.per.gainDesc', {
        amount: formatEuro(versementAnnuel),
        tmi: Math.round(tmi * 100),
      }),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 15 }),
      explanation: t('conseillerPatrimonial.actionItems.per.explanation', {
        tmi: Math.round(tmi * 100),
      }),
      cta: t('conseillerPatrimonial.actionItems.per.cta'),
      projetId: 'p-per',
      bestFor: [
        t('conseillerPatrimonial.actionItems.per.bestFor1'),
        t('conseillerPatrimonial.actionItems.per.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.per.tag1'),
        t('conseillerPatrimonial.actionItems.per.tag2'),
        t('conseillerPatrimonial.actionItems.per.tag3'),
      ],
    })
  }

  // 3) Assurance-vie si pas dispo
  if (!hasAv && monthlyCashflow > 50) {
    const versement = Math.max(Math.round(monthlyCashflow * 0.2), 100)
    actions.push({
      id: 'a-av',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.av.title'),
      category: t('conseillerPatrimonial.actionItems.categories.diversification'),
      impact: 8,
      facilite: 8,
      horizon: 'short',
      horizonLabel: t('conseillerPatrimonial.actionItems.av.horizonLabel'),
      gainAnnuel: Math.round(versement * 12 * 0.04),
      gainDescription: t('conseillerPatrimonial.actionItems.av.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 10 }),
      explanation: t('conseillerPatrimonial.actionItems.av.explanation'),
      cta: t('conseillerPatrimonial.actionItems.av.cta'),
      projetId: 'p-av',
      bestFor: [
        t('conseillerPatrimonial.actionItems.av.bestFor1'),
        t('conseillerPatrimonial.actionItems.av.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.av.tag1'),
        t('conseillerPatrimonial.actionItems.av.tag2'),
        t('conseillerPatrimonial.actionItems.av.tag3'),
      ],
    })
  }

  // 4) Virement automatique si cashflow > 0 et pas encore évoqué
  if (monthlyCashflow > 200) {
    const monthlyAmt = Math.round(monthlyCashflow * 0.5)
    actions.push({
      id: 'a-virement',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.virement.title', { amount: monthlyAmt }),
      category: t('conseillerPatrimonial.actionItems.categories.comportement'),
      impact: 7,
      facilite: 10,
      horizon: 'immediate',
      horizonLabel: t('conseillerPatrimonial.actionItems.virement.horizonLabel'),
      gainAnnuel: monthlyAmt * 12,
      gainDescription: t('conseillerPatrimonial.actionItems.virement.gainDesc', {
        amount: formatEuro(monthlyAmt),
        result: formatEuro(Math.round(monthlyAmt * 12 * 10 * 1.22)),
      }),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 8 }),
      explanation: t('conseillerPatrimonial.actionItems.virement.explanation'),
      cta: t('conseillerPatrimonial.actionItems.virement.cta'),
      projetId: 'p-virement',
      bestFor: [
        t('conseillerPatrimonial.actionItems.virement.bestFor1'),
        t('conseillerPatrimonial.actionItems.virement.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.virement.tag1'),
        t('conseillerPatrimonial.actionItems.virement.tag2'),
        t('conseillerPatrimonial.actionItems.virement.tag3'),
      ],
    })
  }

  // 5) Achat RP si capacité d'emprunt (cashflow > 1000 et patrimoine non négatif)
  if (
    monthlyCashflow > 800 &&
    patrimoineNetValue >= -1000 &&
    (repartition._absolute?.immobilier || 0) === 0
  ) {
    const capaciteEmprunt = Math.round(monthlyIncome * 0.33 * 12 * 25)
    actions.push({
      id: 'a-immo',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.immo.title'),
      category: t('conseillerPatrimonial.actionItems.categories.immobilier'),
      impact: 9,
      facilite: 5,
      horizon: 'medium',
      horizonLabel: t('conseillerPatrimonial.actionItems.immo.horizonLabel'),
      gainAnnuel: Math.round(monthlyExpenses * 12 * 0.4),
      gainDescription: t('conseillerPatrimonial.actionItems.immo.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 18 }),
      explanation: t('conseillerPatrimonial.actionItems.immo.explanation', {
        amount: formatEuro(capaciteEmprunt),
      }),
      cta: t('conseillerPatrimonial.actionItems.immo.cta'),
      projetId: 'p-immo',
      bestFor: [
        t('conseillerPatrimonial.actionItems.immo.bestFor1'),
        t('conseillerPatrimonial.actionItems.immo.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.immo.tag1'),
        t('conseillerPatrimonial.actionItems.immo.tag2'),
        t('conseillerPatrimonial.actionItems.immo.tag3'),
      ],
    })
  }

  // 6) PEA pour croissance
  if (!hasPea && monthlyCashflow > 100 && age && age < 60) {
    const versement = Math.max(Math.round(monthlyCashflow * 0.15), 100)
    actions.push({
      id: 'a-pea',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.pea.title', { amount: versement }),
      category: t('conseillerPatrimonial.actionItems.categories.marches'),
      impact: 8,
      facilite: 7,
      horizon: 'short',
      horizonLabel: t('conseillerPatrimonial.actionItems.pea.horizonLabel'),
      gainAnnuel: Math.round(versement * 12 * 0.07),
      gainDescription: t('conseillerPatrimonial.actionItems.pea.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 12 }),
      explanation: t('conseillerPatrimonial.actionItems.pea.explanation'),
      cta: t('conseillerPatrimonial.actionItems.pea.cta'),
      projetId: 'p-pea',
      bestFor: [
        t('conseillerPatrimonial.actionItems.pea.bestFor1'),
        t('conseillerPatrimonial.actionItems.pea.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.pea.tag1'),
        t('conseillerPatrimonial.actionItems.pea.tag2'),
        t('conseillerPatrimonial.actionItems.pea.tag3'),
      ],
    })
  }

  // 7) Définir des objectifs
  if (objectifsDefinis.length === 0) {
    actions.push({
      id: 'a-objectifs',
      rank: actions.length + 1,
      title: t('conseillerPatrimonial.actionItems.objectifs.title'),
      category: t('conseillerPatrimonial.actionItems.categories.pilotage'),
      impact: 6,
      facilite: 10,
      horizon: 'immediate',
      horizonLabel: t('conseillerPatrimonial.actionItems.objectifs.horizonLabel'),
      gainAnnuel: 0,
      gainDescription: t('conseillerPatrimonial.actionItems.objectifs.gainDesc'),
      scoreImpact: t('conseillerPatrimonial.actionItems.scoreImpact', { n: 8 }),
      explanation: t('conseillerPatrimonial.actionItems.objectifs.explanation'),
      cta: t('conseillerPatrimonial.actionItems.objectifs.cta'),
      projetId: 'p-objectifs',
      bestFor: [
        t('conseillerPatrimonial.actionItems.objectifs.bestFor1'),
        t('conseillerPatrimonial.actionItems.objectifs.bestFor2'),
      ],
      tags: [
        t('conseillerPatrimonial.actionItems.objectifs.tag1'),
        t('conseillerPatrimonial.actionItems.objectifs.tag2'),
      ],
    })
  }

  return actions.slice(0, 7)
}

function buildProjets({ t, monthlyCashflow, repartition, age, monthlyExpenses, city }) {
  const projets = []
  const cashflow = Math.max(monthlyCashflow, 0)
  const monthlyExpensesSafe = monthlyExpenses || 1500

  const durationMonths = Math.max(
    Math.round((monthlyExpensesSafe * 6) / Math.max(cashflow * 0.3, 100)),
    12,
  )

  projets.push({
    id: 'p-livret',
    title: t('conseillerPatrimonial.projetItems.livret.title'),
    icon: 'Savings',
    targetAmount: Math.round(monthlyExpensesSafe * 6),
    monthlyContribution: Math.max(Math.round(cashflow * 0.3), 100),
    duration: t('conseillerPatrimonial.projetItems.livret.duration', { n: durationMonths }),
    expectedYield: t('conseillerPatrimonial.projetItems.livret.yield'),
    vehicle: t('conseillerPatrimonial.projetItems.livret.vehicle'),
    pillar: t('conseillerPatrimonial.projetItems.livret.pillar'),
    color: '#22c55e',
    description: t('conseillerPatrimonial.projetItems.livret.description', {
      amount: formatEuro(monthlyExpensesSafe * 6),
    }),
    autofilled: {
      objectiveType: 'necessaire_fixe',
      duration_months: 60,
      monthly_amount: Math.max(Math.round(cashflow * 0.3), 100),
      target_amount: Math.round(monthlyExpensesSafe * 6),
      vehicles: ['livret_a', 'ldds'],
    },
  })

  if (cashflow > 200) {
    const yearsToRetirement = age ? Math.max(62 - age, 5) : null
    projets.push({
      id: 'p-per',
      title: t('conseillerPatrimonial.projetItems.per.title'),
      icon: 'Elderly',
      targetAmount: 200000,
      monthlyContribution: Math.max(Math.round(cashflow * 0.4), 200),
      duration: yearsToRetirement
        ? t('conseillerPatrimonial.projetItems.per.durationYears', { n: yearsToRetirement })
        : t('conseillerPatrimonial.projetItems.per.durationDefault'),
      expectedYield: t('conseillerPatrimonial.projetItems.per.yield'),
      vehicle: t('conseillerPatrimonial.projetItems.per.vehicle'),
      pillar: t('conseillerPatrimonial.projetItems.per.pillar'),
      color: '#8b5cf6',
      description: t('conseillerPatrimonial.projetItems.per.description'),
      autofilled: {
        objectiveType: 'secondaire_fixe',
        duration_months: age ? Math.max((62 - age) * 12, 60) : 300,
        monthly_amount: Math.max(Math.round(cashflow * 0.4), 200),
        target_amount: 200000,
        vehicles: ['per_individuel'],
        tax_optimized: true,
      },
    })
  }

  if (cashflow > 100) {
    projets.push({
      id: 'p-av',
      title: t('conseillerPatrimonial.projetItems.av.title'),
      icon: 'Shield',
      targetAmount: 50000,
      monthlyContribution: Math.max(Math.round(cashflow * 0.2), 100),
      duration: t('conseillerPatrimonial.projetItems.av.duration'),
      expectedYield: t('conseillerPatrimonial.projetItems.av.yield'),
      vehicle: t('conseillerPatrimonial.projetItems.av.vehicle'),
      pillar: t('conseillerPatrimonial.projetItems.av.pillar'),
      color: '#0ea5e9',
      description: t('conseillerPatrimonial.projetItems.av.description'),
      autofilled: {
        objectiveType: 'secondaire_variable',
        duration_months: 96,
        monthly_amount: Math.max(Math.round(cashflow * 0.2), 100),
        vehicles: ['assurance_vie'],
      },
    })
  }

  if (cashflow > 800 && (repartition._absolute?.immobilier || 0) === 0) {
    projets.push({
      id: 'p-immo',
      title: city
        ? t('conseillerPatrimonial.projetItems.immo.titleWithCity', { city })
        : t('conseillerPatrimonial.projetItems.immo.titleNoCity'),
      icon: 'Home',
      targetAmount: Math.round(cashflow * 12 * 25 * 0.7),
      monthlyContribution: Math.round(cashflow * 0.6),
      duration: t('conseillerPatrimonial.projetItems.immo.duration'),
      expectedYield: t('conseillerPatrimonial.projetItems.immo.yield'),
      vehicle: t('conseillerPatrimonial.projetItems.immo.vehicle'),
      pillar: t('conseillerPatrimonial.projetItems.immo.pillar'),
      color: '#f59e0b',
      description: t('conseillerPatrimonial.projetItems.immo.description'),
      autofilled: {
        objectiveType: 'necessaire_fixe',
        duration_months: 300,
        monthly_amount: Math.round(cashflow * 0.6),
        target_amount: Math.round(cashflow * 12 * 25 * 0.7),
        vehicles: ['pret_immobilier'],
      },
    })
  }

  if (cashflow > 100 && age && age < 60) {
    projets.push({
      id: 'p-pea',
      title: t('conseillerPatrimonial.projetItems.pea.title'),
      icon: 'ShowChart',
      targetAmount: 80000,
      monthlyContribution: Math.max(Math.round(cashflow * 0.15), 100),
      duration: t('conseillerPatrimonial.projetItems.pea.duration'),
      expectedYield: t('conseillerPatrimonial.projetItems.pea.yield'),
      vehicle: t('conseillerPatrimonial.projetItems.pea.vehicle'),
      pillar: t('conseillerPatrimonial.projetItems.pea.pillar'),
      color: '#ec4899',
      description: t('conseillerPatrimonial.projetItems.pea.description'),
      autofilled: {
        objectiveType: 'secondaire_variable',
        duration_months: 180,
        monthly_amount: Math.max(Math.round(cashflow * 0.15), 100),
        vehicles: ['pea', 'etf_world'],
      },
    })
  }

  projets.push({
    id: 'p-prevoyance',
    title: t('conseillerPatrimonial.projetItems.prevoyance.title'),
    icon: 'Shield',
    targetAmount: 0,
    monthlyContribution: 35,
    duration: t('conseillerPatrimonial.projetItems.prevoyance.duration'),
    expectedYield: t('conseillerPatrimonial.projetItems.prevoyance.yield'),
    vehicle: t('conseillerPatrimonial.projetItems.prevoyance.vehicle'),
    pillar: t('conseillerPatrimonial.projetItems.prevoyance.pillar'),
    color: '#22c55e',
    description: t('conseillerPatrimonial.projetItems.prevoyance.description'),
    autofilled: {
      objectiveType: 'necessaire_fixe',
      duration_months: 0,
      monthly_amount: 35,
      vehicles: ['prevoyance'],
    },
  })

  return projets.slice(0, 6)
}

function buildProjection20ans({ patrimoineNetValue, monthlyCashflow }) {
  const start = patrimoineNetValue || 0
  // Sans action : suit l'évolution actuelle (cashflow * 12 si positif, sinon stagne)
  const annualDelta = Math.max(0, (monthlyCashflow || 0) * 12 * 0.2) // 20% de l'épargne mis de côté en moyenne
  const actuel = []
  for (let y = 0; y <= 20; y++) {
    actuel.push({ year: 2026 + y, value: Math.round(start + annualDelta * y) })
  }
  // Avec actions : 80% de l'épargne investie à 4.5% capitalisés
  const annualContrib = Math.max(0, (monthlyCashflow || 0) * 12 * 0.8)
  const avecActions = []
  let val = start
  for (let y = 0; y <= 20; y++) {
    avecActions.push({ year: 2026 + y, value: Math.round(val) })
    val = val * 1.045 + annualContrib
  }
  return { actuel, avecActions }
}

function buildSuggestedPrompts({ t, objectifsDefinis, age, repartition, monthlyCashflow }) {
  const prompts = []
  if (monthlyCashflow > 0) {
    prompts.push(
      t('conseillerPatrimonial.suggestedPrompts.canSave', {
        amount: formatEuro(monthlyCashflow),
      }),
    )
  }
  prompts.push(t('conseillerPatrimonial.suggestedPrompts.increaseScore'))
  if ((repartition._absolute?.per || 0) === 0) {
    prompts.push(t('conseillerPatrimonial.suggestedPrompts.openPer'))
  }
  if ((repartition._absolute?.immobilier || 0) === 0 && monthlyCashflow > 500) {
    prompts.push(t('conseillerPatrimonial.suggestedPrompts.buyHome'))
  }
  if (age && age < 35) {
    prompts.push(t('conseillerPatrimonial.suggestedPrompts.strategyForAge'))
  }
  if (objectifsDefinis.length === 0) {
    prompts.push(t('conseillerPatrimonial.suggestedPrompts.prioritizeObjectives'))
  }
  return prompts.slice(0, 5)
}

// completenessScore retiré : on raisonne maintenant champ par champ
// directement via les booléens has* exposés dans le retour.
